import pRetry from 'p-retry';
import {
  isAudioWorkEnabled,
  trackDetachedAudioWork,
  type AudioWorkContext,
} from '../../audioWorkBarrier';
import { getSafeErrorFields, isSessionNotFoundError } from '../../errors';
import {
  addBackup,
  getRecordingRow,
  markBackupFailed,
  markBackupUploaded,
  markRecordingAbandoned,
  peekRecordingRow,
} from '../../persistenceV2';
// Direct: the mocked `../../persistenceV2` door omits the predicate in suites
// that only stub the store functions.
import { isMemoryOnlyWriteError } from '../../persistenceV2/writeFence';
import { type BackupRecording } from '../../types';
import { deserializeBlob, getErrorInfo } from '../../utils';
import { logStorePersistFailure } from '../lib/logStorePersistFailure';
import { logger } from '../lib/logger';
import { simpleRetry } from '../simpleRetry';
import {
  backupUploadKey,
  beginExclusiveUpload,
  endExclusiveUpload,
} from './persistRegistry';
import { maybeCompleteRecordingV2 } from './processChunkV2';
import {
  isRecoveryDeferredError,
  isRecoveryUploadTimeoutError,
  RECOVERY_BACKUP_UPLOAD_TIMEOUT_MS,
  RECOVERY_CONNECTIVITY_WAIT_MS,
  uploadBounded,
  waitForConnectivityBounded,
} from './recoveryBounds';
import { requireConfig, type GetState, type SetState } from './storeTypes';

/** Recovery replays another page's rows, so only it takes the bounded waits. */
export type BackupDispatchSource = 'capture' | 'recovery';
/** `deferred`: a recovery dispatch that sent nothing for want of connectivity. */
export type BackupDispatchOutcome = 'dispatched' | 'deduped' | 'deferred';

export const BACKUP_METADATA_READ_TIMEOUT_MS = 1000;

const getRecordingRowBounded = async (
  key: { sessionId: string; recordingId: string },
  signal?: AbortSignal
) => {
  signal?.throwIfAborted();
  let timer: ReturnType<typeof setTimeout> | undefined;
  let onAbort: (() => void) | undefined;
  const timeout = new Promise<never>((_, reject) => {
    timer = setTimeout(
      () => reject(new Error('backup metadata read timed out')),
      BACKUP_METADATA_READ_TIMEOUT_MS
    );
  });
  const aborted = new Promise<never>((_, reject) => {
    if (!signal) return;
    onAbort = () =>
      reject(
        signal.reason instanceof Error
          ? signal.reason
          : new DOMException('Aborted', 'AbortError')
      );
    signal.addEventListener('abort', onAbort, { once: true });
  });
  try {
    return await Promise.race([getRecordingRow(key), timeout, aborted]);
  } finally {
    clearTimeout(timer);
    if (signal && onAbort) signal.removeEventListener('abort', onAbort);
  }
};

/** Uploads from memory even when persistence fails. */
export const handleBackupAvailableV2 = async (
  get: GetState,
  backup: BackupRecording,
  context?: AudioWorkContext
) => {
  const { sessionId, recordingId } = backup;
  const blob = deserializeBlob(backup.blob);

  const persist = (async () => {
    try {
      context?.throwIfStale();
      if (!blob) throw new Error('backup blob missing at persist time');
      await simpleRetry(() => addBackup({ sessionId, recordingId }, blob));
      context?.throwIfStale();
    } catch (err) {
      if (context && !context.isCurrent()) return;
      // A memory-only refusal is the start's storage-less event repeated, not a
      // fault: error level here buries the real ones for the whole recording.
      logStorePersistFailure(
        {
          event_type: 'ADD_BACKUP_ERROR',
          recording_id: recordingId,
          details: await getErrorInfo(err).catch(() => ({ message: String(err) })),
        },
        { memoryOnly: isMemoryOnlyWriteError(err) }
      );
    }
  })();
  const upload = context
    ? get().processBackup(backup, context)
    : get().processBackup(backup);

  // Upload dispatch never gates on IndexedDB (above); the recorder tail
  // settles at persist (bounded by stop's tail wait), while the upload
  // continues detached but stays in the logout-drain set.
  // Tagged so the stop-tail drain can release the recording lock without
  // waiting on a slow backup upload (logout's unfiltered drain still waits).
  trackDetachedAudioWork(upload, 'backup-upload');
  await persist;
};

/** Uploads once, then records success separately to avoid duplicate uploads. */
export const processBackupV2 = async (
  set: SetState,
  get: GetState,
  backup: BackupRecording,
  context?: AudioWorkContext,
  source: BackupDispatchSource = 'capture'
): Promise<BackupDispatchOutcome> => {
  // Chokepoint dedupe, mirroring processChunkV2: one active upload per backup.
  const uploadKey = backupUploadKey(backup);
  if (!beginExclusiveUpload(uploadKey)) {
    logger.info({
      event_type: 'BACKUP_UPLOAD_DEDUPED_IN_FLIGHT',
      details: { sessionId: backup.sessionId, recordingId: backup.recordingId },
    });
    return 'deduped';
  }
  try {
    return await runProcessBackupV2(set, get, backup, context, source);
  } finally {
    endExclusiveUpload(uploadKey);
  }
};

const runProcessBackupV2 = async (
  set: SetState,
  get: GetState,
  backup: BackupRecording,
  context: AudioWorkContext | undefined,
  source: BackupDispatchSource
): Promise<'dispatched' | 'deferred'> => {
  const { sessionId, recordingId } = backup;
  const { waitForConnectivity, cleanupRecordingsTask } = get();
  const bounds =
    source === 'recovery'
      ? {
          connectivity: RECOVERY_CONNECTIVITY_WAIT_MS,
          upload: RECOVERY_BACKUP_UPLOAD_TIMEOUT_MS,
        }
      : undefined;
  const key = { sessionId, recordingId };
  const fallbackDate = new Date();
  let startDate = fallbackDate;
  let endDate = fallbackDate;

  try {
    // Mirror first: a live recording needs no database read for its dates.
    const recording =
      peekRecordingRow(key) ?? (await getRecordingRowBounded(key, context?.signal));
    startDate = recording?.startDate ?? fallbackDate;
    endDate = recording?.endDate ?? fallbackDate;
  } catch (err) {
    if (!isAudioWorkEnabled(context)) return 'dispatched';
    logger.warn({
      event_type: 'BACKUP_METADATA_READ_FAILED',
      details: { recordingId, ...getSafeErrorFields(err) },
    });
  }

  try {
    const config = requireConfig(get);
    await pRetry(
      async () => {
        context?.throwIfStale();
        await waitForConnectivityBounded(
          waitForConnectivity,
          context?.signal,
          bounds?.connectivity
        );
        context?.throwIfStale();
        const backupToUpload = {
          ...backup,
          startDate,
          endDate,
        };
        await uploadBounded(
          (signal) =>
            signal
              ? config.uploadBackup(backupToUpload, signal)
              : config.uploadBackup(backupToUpload),
          context?.signal,
          bounds?.upload
        );
      },
      {
        retries: 2,
        signal: context?.signal,
        // A timed-out backup may have landed, and no backup delivery is known idempotent.
        shouldRetry: (err) =>
          !isSessionNotFoundError(err) &&
          !isRecoveryDeferredError(err) &&
          !isRecoveryUploadTimeoutError(err),
      }
    );

    try {
      if (isAudioWorkEnabled(context)) {
        await simpleRetry(() =>
          markBackupUploaded(key, { deleteBlob: !config.retainData })
        );
      }
    } catch (err) {
      // Same demotion its chunk twin takes: under memory-only the mirror already
      // holds the status, so an error per backup buries the real faults.
      logStorePersistFailure(
        {
          event_type: 'MARK_BACKUP_UPLOADED_ERROR',
          details: { recordingId, ...getSafeErrorFields(err) },
        },
        { memoryOnly: isMemoryOnlyWriteError(err) }
      );
    }
    if (!isAudioWorkEnabled(context)) return 'dispatched';
    await maybeCompleteRecordingV2(set, get, key, context);
  } catch (err) {
    if (!isAudioWorkEnabled(context)) return 'dispatched';
    if (isRecoveryDeferredError(err)) {
      logger.info({
        event_type: 'RECOVERY_BACKUP_DEFERRED_OFFLINE',
        details: { recordingId },
      });
      return 'deferred';
    }
    logger.error({
      event_type: 'UPLOAD_BACKUP_FAILURE',
      details: { recordingId, ...getSafeErrorFields(err) },
    });

    try {
      if (isAudioWorkEnabled(context)) {
        if (isSessionNotFoundError(err)) {
          await simpleRetry(() => markRecordingAbandoned(key));
        } else {
          await simpleRetry(() => markBackupFailed(key));
        }
        if (isAudioWorkEnabled(context)) {
          await maybeCompleteRecordingV2(set, get, key, context);
        }
      }
    } catch (persistErr) {
      logStorePersistFailure(
        {
          event_type: 'MARK_BACKUP_FAILED_ERROR',
          details: { recordingId, ...getSafeErrorFields(persistErr) },
        },
        { memoryOnly: isMemoryOnlyWriteError(persistErr) }
      );
    }
  }

  if (!isAudioWorkEnabled(context)) return 'dispatched';
  void cleanupRecordingsTask();
  set((state) => ({
    backupCaptureSlot:
      state.backupCaptureSlot?.recordingId === recordingId
        ? null
        : state.backupCaptureSlot,
  }));
  return 'dispatched';
};
